/**
 * ReportComments — comment list + optional form (create/edit own comment).
 * readOnly=true: list only, no form (used on public /r/:token page).
 * readOnly=false: list + create form + edit own comment inline.
 * prop `currentUserId` needed to show "Sửa" button on own comments.
 */
import { useState } from 'react';
import { Pencil, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';
import type { PortalComment } from '@client/lib/queries/use-portal';

interface ReportCommentsProps {
  comments: PortalComment[];
  reportId: string;
  readOnly: boolean;
  currentUserId?: string;
  onSubmit?: (content: string) => Promise<void>;
  onEdit?: (commentId: string, content: string) => Promise<void>;
}

export function ReportComments({
  comments,
  reportId: _reportId,
  readOnly,
  currentUserId,
  onSubmit,
  onEdit,
}: ReportCommentsProps) {
  const [newContent, setNewContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = newContent.trim();
    if (!trimmed || trimmed.length > 2000) return;
    setSubmitting(true);
    try {
      await onSubmit?.(trimmed);
      setNewContent('');
      toast.success('Đã gửi phản hồi');
    } catch (err) {
      const e = err as { status?: number; message?: string };
      if (e.status === 429) {
        toast.error('Bạn gửi phản hồi quá nhanh, thử lại sau ít phút');
      } else {
        toast.error(e.message ?? 'Gửi thất bại');
      }
    } finally {
      setSubmitting(false);
    }
  }

  function startEdit(comment: PortalComment) {
    setEditingId(comment.id);
    setEditContent(comment.content);
  }

  async function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!editingId) return;
    const trimmed = editContent.trim();
    if (!trimmed || trimmed.length > 2000) return;
    setSubmitting(true);
    try {
      await onEdit?.(editingId, trimmed);
      setEditingId(null);
      toast.success('Đã cập nhật phản hồi');
    } catch (err) {
      const e = err as { message?: string };
      toast.error(e.message ?? 'Cập nhật thất bại');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4">
      <h3 className="text-base font-semibold">Phản hồi ({comments.length})</h3>

      {comments.length === 0 && (
        <p className="text-sm text-muted-foreground py-4 text-center border border-dashed rounded-lg">
          Chưa có phản hồi nào
        </p>
      )}

      <div className="space-y-3">
        {comments.map((c) => {
          const isOwn = currentUserId && c.authorId === currentUserId;
          const isEditing = editingId === c.id;

          return (
            <div key={c.id} className="border border-border rounded-lg p-3 space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-semibold text-primary">
                    {(c.authorName ?? 'U').charAt(0).toUpperCase()}
                  </div>
                  <span className="text-sm font-medium">{c.authorName ?? 'Người dùng'}</span>
                  {c.editedAt && (
                    <span className="text-xs text-muted-foreground italic">(đã chỉnh sửa)</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(c.createdAt), { locale: vi, addSuffix: true })}
                  </span>
                  {!readOnly && isOwn && !isEditing && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => startEdit(c)}
                      aria-label="Sửa phản hồi"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>

              {isEditing ? (
                <form onSubmit={(e) => void handleEditSubmit(e)} className="space-y-2">
                  <Textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={3}
                    maxLength={2000}
                    className="text-sm resize-none"
                    disabled={submitting}
                  />
                  <div className="flex gap-2 justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingId(null)}
                      disabled={submitting}
                    >
                      Hủy
                    </Button>
                    <Button type="submit" size="sm" disabled={submitting || !editContent.trim()}>
                      {submitting ? 'Đang lưu...' : 'Lưu'}
                    </Button>
                  </div>
                </form>
              ) : (
                <p className="text-sm text-foreground whitespace-pre-wrap">{c.content}</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Submit form — hidden when readOnly */}
      {!readOnly && (
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-2">
          <Textarea
            placeholder="Nhập phản hồi của bạn..."
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            rows={3}
            maxLength={2000}
            className="text-sm resize-none w-full"
            disabled={submitting}
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              {newContent.length}/2000
            </span>
            <Button
              type="submit"
              size="sm"
              disabled={submitting || !newContent.trim() || newContent.trim().length > 2000}
              className="min-w-[80px] min-h-[44px]"
            >
              {submitting ? (
                'Đang gửi...'
              ) : (
                <>
                  <Send className="h-4 w-4 mr-1.5" />
                  Gửi
                </>
              )}
            </Button>
          </div>
        </form>
      )}

      {/* Public page: redirect to login for feedback */}
      {readOnly && (
        <a
          href="/login"
          className="flex items-center justify-center w-full min-h-[44px] rounded-md border border-border bg-background text-sm font-medium hover:bg-accent transition-colors gap-2"
        >
          <Send className="h-4 w-4" />
          Gửi phản hồi
        </a>
      )}
    </div>
  );
}
